import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, RiskBadge, StatusBadge, EmptyState } from "@/components/common/Bits";
import { fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Scale, CheckCircle2, XCircle, Vote, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const VOTE_OPTIONS = ["Aprobar", "Aprobar con condiciones", "Solicitar antecedentes", "Escalar a DPO", "Escalar a Seguridad", "Escalar a revisión clínica", "Rechazar"];

export default function Committee() {
  const { user, can } = useAuth();
  const [queue, setQueue] = useState(null);

  const load = () => api.get("/committee/queue").then(({ data }) => setQueue(data));
  useEffect(() => { load(); }, []);

  if (!queue) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>;

  return (
    <div>
      <PageHeader title="Comité de IA" subtitle="Revisión del dossier, declaración de conflictos de interés, votación con quórum y registro de condiciones." testId="committee-header" />
      {queue.length === 0 && <EmptyState icon={Scale} title="Sin iniciativas en decisión" />}
      <div className="space-y-5">
        {queue.map((d) => {
          const myVote = d.votes.find((v) => v.voter_id === user.id);
          const open = d.status === "En votación";
          return (
            <Card key={d.id} className="shadow-card border-border" data-testid={`decision-card-${d.initiative_id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge status={d.status} />
                      {d.initiative && <RiskBadge level={d.initiative.residual_risk} />}
                      {d.initiative?.clinical_data && <span className="text-[11px] px-2 py-0.5 rounded-md bg-risk-critical/10 text-risk-critical border border-risk-critical/25 font-medium">Iniciativa clínica · aprobaciones independientes</span>}
                    </div>
                    <Link to={`/iniciativas/${d.initiative_id}`} className="group inline-flex items-center gap-1.5 mt-2">
                      <h2 className="font-display text-lg font-semibold group-hover:text-primary transition-colors">{d.initiative_name}</h2>
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                    </Link>
                    <p className="text-xs text-muted-foreground mt-1">{d.initiative?.department} · {d.initiative?.provider} · {d.initiative?.model}</p>

                    <div className="mt-4 rounded-lg border border-border bg-gradient-panel p-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Estado del dossier</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <span className="flex items-center gap-1.5">{d.dossier.items.assessments > 0 ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-risk-high" />} Evaluación ({d.dossier.items.assessments})</span>
                        <span className="flex items-center gap-1.5">{d.dossier.items.controls > 0 ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-risk-high" />} Controles ({d.dossier.items.controls})</span>
                        <span className="flex items-center gap-1.5">{d.dossier.items.documents > 0 ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-risk-high" />} Evidencias ({d.dossier.items.documents})</span>
                      </div>
                      {!d.dossier.complete && (
                        <p className="text-xs text-risk-medium mt-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> {d.dossier.missing.join(" · ")}</p>
                      )}
                    </div>

                    {d.conditions?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Condiciones registradas</p>
                        <ul className="text-xs mt-1.5 space-y-1">{d.conditions.map((c) => <li key={c} className="flex gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-risk-medium shrink-0 mt-0.5" />{c}</li>)}</ul>
                      </div>
                    )}
                  </div>

                  <div className="lg:w-80 shrink-0 flex flex-col">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Votos · quórum {d.votes.length}/{d.quorum_required}</p>
                    <div className="space-y-2 flex-1">
                      {d.votes.length === 0 && <p className="text-xs text-muted-foreground">Aún no hay votos registrados.</p>}
                      {d.votes.map((v) => (
                        <div key={v.id} className="rounded-lg border border-border p-2.5">
                          <div className="flex justify-between items-center gap-2">
                            <p className="text-xs font-semibold truncate">{v.voter}</p>
                            <span className="text-[10px] text-muted-foreground shrink-0">{fmtDate(v.date)}</span>
                          </div>
                          <p className="text-[11px] text-primary font-medium mt-0.5">{v.vote}</p>
                          <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{v.justification}</p>
                        </div>
                      ))}
                    </div>
                    {open && can.vote && !myVote && d.dossier.complete && (
                      <VoteDialog decision={d} onVoted={load} />
                    )}
                    {open && can.vote && myVote && <p className="text-xs text-success mt-3 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Su voto ya fue registrado</p>}
                    {open && !can.vote && <p className="text-xs text-muted-foreground mt-3">Su rol no participa en la votación.</p>}
                    {open && can.vote && !d.dossier.complete && <p className="text-xs text-risk-medium mt-3">No se puede votar con dossier incompleto.</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

const VoteDialog = ({ decision, onVoted }) => {
  const [open, setOpen] = useState(false);
  const [vote, setVote] = useState("");
  const [justification, setJustification] = useState("");
  const [conditions, setConditions] = useState("");
  const [conflict, setConflict] = useState(false);
  const [conflictReason, setConflictReason] = useState("");

  const declareConflict = async () => {
    if (!conflictReason) { toast.error("Describa el conflicto de interés"); return; }
    try {
      const { data } = await api.post(`/committee/${decision.id}/conflict`, { reason: conflictReason });
      toast.info(data.message);
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    try {
      const { data } = await api.post(`/committee/${decision.id}/vote`, {
        vote, justification,
        conditions: conditions ? conditions.split("\n").map((s) => s.trim()).filter(Boolean) : [],
        conflict_of_interest: conflict,
      });
      toast.success(data.final_status ? `Quórum alcanzado · decisión: ${data.final_status}` : `Voto registrado (${data.votes_count}/${data.quorum_required})`);
      setOpen(false);
      onVoted();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="mt-3 bg-primary hover:bg-primary/90 text-primary-foreground" data-testid={`vote-btn-${decision.initiative_id}`}>
          <Vote className="h-4 w-4 mr-2" /> Emitir voto
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Votación · {decision.initiative_name}</DialogTitle>
          <DialogDescription>El fundamento es obligatorio. No es posible votar con conflicto de interés declarado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <p className="text-sm font-medium">Declaro conflicto de interés</p>
              <p className="text-[11px] text-muted-foreground">Al declararlo quedará inhabilitado para votar</p>
            </div>
            <Switch checked={conflict} onCheckedChange={setConflict} data-testid="conflict-switch" />
          </div>

          {conflict ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Describa el conflicto</Label>
                <Textarea rows={2} value={conflictReason} onChange={(e) => setConflictReason(e.target.value)} data-testid="conflict-reason-input" />
              </div>
              <Button onClick={declareConflict} variant="outline" className="w-full border-risk-medium/40 text-risk-medium hover:bg-risk-medium/5" data-testid="declare-conflict-btn">
                Registrar abstención por conflicto
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Decisión</Label>
                <Select value={vote} onValueChange={setVote}>
                  <SelectTrigger data-testid="vote-select"><SelectValue placeholder="Seleccionar voto" /></SelectTrigger>
                  <SelectContent>{VOTE_OPTIONS.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Fundamento (obligatorio)</Label>
                <Textarea rows={3} value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="Fundamento de la decisión para el registro de auditoría" data-testid="vote-justification-input" />
              </div>
              {vote === "Aprobar con condiciones" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Condiciones (una por línea)</Label>
                  <Textarea rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)} data-testid="vote-conditions-input" />
                </div>
              )}
            </>
          )}
        </div>
        {!conflict && (
          <DialogFooter>
            <Button onClick={submit} disabled={!vote} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="vote-submit-btn">Registrar voto</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
